/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

(() => {
  class HomeStorefront extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.shadeButtons = [...this.querySelectorAll('[data-home-shade]')];
      this.shadeProducts = [...this.querySelectorAll('[data-home-seasonal-product]')];
      this.shadeStatus = this.querySelector('[data-home-shade-status]');
      this.shadeButtons.forEach((button) => {
        button.addEventListener('click', () => this.showShade(button.dataset.homeShade, true), { signal });
      });

      this.motionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.celebrities = this.querySelector('[data-home-celebrities]');
      this.celebritiesToggle = this.querySelector('[data-home-celebrities-toggle]');
      this.celebritiesToggle?.addEventListener('click', () => {
        this.setCelebritiesPaused(!this.celebrities.classList.contains('is-paused'));
      }, { signal });
      this.motionMedia.addEventListener('change', (event) => {
        this.setCelebritiesPaused(event.matches);
        this.setSocialVideoPlayback();
      }, { signal });
      this.setCelebritiesPaused(this.motionMedia.matches);

      this.socialVideo = this.querySelector('[data-home-social-video]');
      if (this.socialVideo && 'IntersectionObserver' in window) {
        this.socialVideoObserver = new IntersectionObserver(([entry]) => {
          this.socialVideoVisible = entry.isIntersecting;
          this.setSocialVideoPlayback();
        }, { threshold: 0.2 });
        this.socialVideoObserver.observe(this.socialVideo);
        document.addEventListener('visibilitychange', () => this.setSocialVideoPlayback(), { signal });
      } else if (this.socialVideo) {
        this.socialVideoVisible = true;
        this.setSocialVideoPlayback();
      }

      this.revealItems = [...this.querySelectorAll('[data-home-reveal]')];
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && 'IntersectionObserver' in window) {
        this.revealObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            this.revealObserver?.unobserve(entry.target);
          });
        }, { threshold: 0.18 });
        this.revealItems.forEach((item) => this.revealObserver.observe(item));
      } else {
        this.revealItems.forEach((item) => item.classList.add('is-visible'));
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.revealObserver?.disconnect();
      this.socialVideoObserver?.disconnect();
      this.socialVideo?.pause();
    }

    showShade(shade, focus = false) {
      if (!shade) return;
      let activeLabel = '';
      this.shadeButtons.forEach((button) => {
        const active = button.dataset.homeShade === shade;
        button.setAttribute('aria-pressed', String(active));
        if (active) activeLabel = button.textContent.trim();
        if (active && focus) button.focus({ preventScroll: true });
      });
      this.shadeProducts.forEach((product) => {
        product.hidden = product.dataset.homeSeasonalProduct !== shade;
      });
      if (focus && this.shadeStatus) this.shadeStatus.textContent = `${activeLabel} products shown.`;
    }

    setCelebritiesPaused(paused) {
      if (!this.celebrities || !this.celebritiesToggle) return;
      this.celebrities.classList.toggle('is-paused', paused);
      const label = this.celebritiesToggle.querySelector('[data-home-celebrities-toggle-label]');
      if (label) label.textContent = paused ? 'Play animation' : 'Pause animation';
    }

    setSocialVideoPlayback() {
      if (!this.socialVideo) return;
      if (!this.socialVideoVisible || this.motionMedia?.matches || document.hidden) {
        this.socialVideo.pause();
        return;
      }
      this.socialVideo.play().catch(() => {
        // The poster remains visible when the browser declines autoplay.
      });
    }
  }

  if (!customElements.get('home-storefront')) customElements.define('home-storefront', HomeStorefront);
})();
